import {
  addDoc,
  arrayRemove,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
  orderBy,
} from 'firebase/firestore';
import { db } from '../firebase';
import { normalizeAlaCarteItem, computePricingMetrics } from '../utils/alaCarteCatalog';
import {
  normalizeAlaCarteBid,
  normalizePackageItems,
  packageItemsToDeliverableRows,
  sanitizeBidForClient,
  PRICING_STATUS,
  CLIENT_RESPONSE_STATUS,
  departmentToPostedTeam,
  INTERNAL_BID_ACCOUNT_ID,
  INTERNAL_BID_COMPANY,
} from '../utils/alaCarteBid';
import { getAccountById } from './firestoreService';

/** `accountType` + `companyName` stamped on a new bid; internal bids have no account doc. */
async function bidAccountFields(accountId) {
  if (accountId === INTERNAL_BID_ACCOUNT_ID) {
    return { accountType: 'Internal', companyName: INTERNAL_BID_COMPANY.name };
  }
  const account = await getAccountById(accountId);
  return {
    accountType: account?.type === 'Clients' ? 'Clients' : 'Leads',
    companyName: account?.company || '',
  };
}

/**
 * @param {string} accountId
 * @param {string} catalogItemId
 * @param {string} [sourceRequestId]
 */
export async function bidExistsForSource(accountId, catalogItemId, sourceRequestId) {
  if (!sourceRequestId) return false;
  const q = query(
    collection(db, 'alaCarteBids'),
    where('accountId', '==', accountId),
    where('catalogItemId', '==', catalogItemId),
    where('sourceRequestId', '==', sourceRequestId)
  );
  const snap = await getDocs(q);
  return !snap.empty;
}

/**
 * @param {object} opts
 * @param {string} opts.accountId
 * @param {string} opts.catalogItemId
 * @param {'portal'|'crm'} opts.source
 * @param {string} [opts.sourceRequestId]
 * @param {string} [opts.postedByUid]
 * @param {boolean} [opts.postToTeam=true]
 * @param {number} [opts.quantity=1]
 * @param {boolean} [opts.fromProgramSignup=false] The lead picked this still-unpriced item in the
 *   program they signed up for — it was kept off the contract's program items and needs pricing.
 * @param {string} [opts.department] Team the bid belongs to; defaults to the catalog item's department.
 * @param {DirectPricing} [opts.directPricing] Exec-entered price — skips the team queue entirely.
 */
export async function createBidFromCatalogItem({
  accountId,
  catalogItemId,
  source,
  sourceRequestId = '',
  postedByUid = '',
  postToTeam = true,
  quantity = 1,
  fromProgramSignup = false,
  department: departmentOverride = '',
  directPricing = null,
}) {
  if (!accountId || !catalogItemId) {
    throw new Error('accountId and catalogItemId are required');
  }

  if (sourceRequestId) {
    const exists = await bidExistsForSource(accountId, catalogItemId, sourceRequestId);
    if (exists) return null;
  }

  const [accountFields, catalogSnap] = await Promise.all([
    bidAccountFields(accountId),
    getDoc(doc(db, 'alaCarteItems', catalogItemId)),
  ]);

  if (!catalogSnap.exists()) {
    throw new Error('Catalog item not found');
  }

  const catalog = normalizeAlaCarteItem(catalogSnap.id, catalogSnap.data());
  const department = departmentOverride || catalog.department || 'General';
  const team = departmentToPostedTeam(department);
  const now = new Date();

  const payload = {
    accountId,
    ...accountFields,
    catalogItemId,
    department,
    name: catalog.name || '',
    description: catalog.description || '',
    icon: catalog.icon || '📦',
    pricingType: catalog.pricingType || 'caseByCase',
    standardPricing: catalog.standardPricing || { cost: 0, unit: 'item', stripeProductId: '' },
    scope: '',
    deliverableRows: [],
    teamInput: { scope: '', deliverableRows: [], internalCostEstimate: '' },
    internalOnly: { notes: '' },
    bidNegotiation: {
      counterClientPrice: '',
      counterInternalCost: '',
      counterNote: '',
      execDecisionNote: '',
    },
    internalCost: '',
    clientListPrice: '',
    markupPercent: '',
    quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
    fromProgramSignup: fromProgramSignup === true,
    source: source === 'portal' ? 'portal' : 'crm',
    sourceRequestId: sourceRequestId || '',
    createdByUid: postedByUid || '',
    createdAt: now,
    updatedAt: now,
  };

  Object.assign(
    payload,
    directPricing
      ? directPricingFields({ ...directPricing, postedByUid, now })
      : postingFields({ postToTeam, team, postedByUid, now })
  );

  const ref = await addDoc(collection(db, 'alaCarteBids'), payload);
  return ref.id;
}

/**
 * One-off service typed in by an exec — not in the catalog, so no `catalogItemId`.
 * Same doc shape as a catalog bid; `isCustom` marks it for the account Bids tab.
 *
 * @param {object} opts
 * @param {string} opts.accountId
 * @param {string} opts.name
 * @param {string} opts.department Team the bid belongs to.
 * @param {string} [opts.description]
 * @param {string} [opts.icon]
 * @param {string} [opts.postedByUid]
 * @param {boolean} [opts.postToTeam=true]
 * @param {DirectPricing} [opts.directPricing]
 * @returns {Promise<string>} new bid id
 */
export async function createCustomBid({
  accountId,
  name,
  department,
  description = '',
  icon = '✏️',
  postedByUid = '',
  postToTeam = true,
  directPricing = null,
}) {
  if (!accountId) throw new Error('accountId is required');
  const serviceName = String(name || '').trim();
  if (!serviceName) throw new Error('Name the service');
  if (!department) throw new Error('Choose a team');

  const accountFields = await bidAccountFields(accountId);
  const team = departmentToPostedTeam(department);
  const now = new Date();

  const payload = {
    accountId,
    ...accountFields,
    catalogItemId: '',
    isCustom: true,
    department,
    name: serviceName,
    description: String(description || '').trim(),
    icon: String(icon || '').trim() || '✏️',
    pricingType: 'caseByCase',
    standardPricing: { cost: 0, unit: 'item', stripeProductId: '' },
    scope: '',
    deliverableRows: [],
    teamInput: { scope: '', deliverableRows: [], internalCostEstimate: '' },
    internalOnly: { notes: '' },
    bidNegotiation: {
      counterClientPrice: '',
      counterInternalCost: '',
      counterNote: '',
      execDecisionNote: '',
    },
    internalCost: '',
    clientListPrice: '',
    markupPercent: '',
    quantity: 1,
    source: 'crm',
    sourceRequestId: '',
    createdByUid: postedByUid || '',
    createdAt: now,
    updatedAt: now,
    ...(directPricing
      ? directPricingFields({ ...directPricing, postedByUid, now })
      : postingFields({ postToTeam, team, postedByUid, now })),
  };

  const ref = await addDoc(collection(db, 'alaCarteBids'), payload);
  return ref.id;
}

/**
 * @typedef {object} DirectPricing
 * @property {number} clientListPrice What the client pays.
 * @property {number|''} [internalCost] What it costs us; blank when unknown.
 * @property {string} [scope] Scope of work shown on the client's quote.
 * @property {boolean} [publish] true = visible in the client portal now; false = parked in exec review.
 */

/** Exec priced it themselves: no team hand-off, straight to exec review or published. */
function directPricingFields({ clientListPrice, internalCost = '', scope = '', publish = false, postedByUid, now }) {
  const client = Number(clientListPrice);
  if (!Number.isFinite(client) || client < 0) throw new Error('Enter a valid client price');
  const cost = internalCost === '' || internalCost == null ? '' : Number(internalCost);
  if (cost !== '' && (!Number.isFinite(cost) || cost < 0)) throw new Error('Enter a valid internal cost');
  const { markupPercent } = cost === '' ? { markupPercent: null } : computePricingMetrics(cost, client);
  const trimmedScope = String(scope || '').trim();

  return {
    pricingStatus: publish ? PRICING_STATUS.published : PRICING_STATUS.exec_ready,
    clientListPrice: client,
    internalCost: cost,
    markupPercent: markupPercent != null && Number.isFinite(markupPercent) ? Math.round(markupPercent * 100) / 100 : '',
    ...(trimmedScope ? { scope: trimmedScope } : {}),
    pricedDirectlyByExec: true,
    postedToTeam: null,
    postedAt: null,
    postedByUid: null,
    teamSubmittedAt: null,
    ...(publish ? { publishedAt: now, publishedByUid: postedByUid || '' } : {}),
  };
}

/** Status/routing fields — a bid either goes straight out to the team or stays a draft. */
function postingFields({ postToTeam, team, postedByUid, now }) {
  if (!postToTeam) {
    return {
      pricingStatus: PRICING_STATUS.draft,
      postedToTeam: null,
      postedAt: null,
      postedByUid: null,
      teamSubmittedAt: null,
    };
  }
  return {
    pricingStatus: PRICING_STATUS.awaiting_team,
    postedToTeam: team,
    postedAt: now,
    postedByUid: postedByUid || '',
    teamSubmittedAt: null,
  };
}

/**
 * Creates one bid covering several services — catalog picks, custom lines, or a mix.
 * The team prices and the client signs the package as a single quote, so it is stored
 * as a normal bid doc (no `catalogItemId`) with the bundle recorded on `packageItems`.
 *
 * @param {object} opts
 * @param {string} opts.accountId
 * @param {string} opts.name
 * @param {Array<{catalogItemId?: string, name: string, notes?: string, icon?: string, department?: string, quantity?: number}>} opts.items
 * @param {string} [opts.description]
 * @param {string} [opts.icon]
 * @param {string} [opts.department] Team that prices the package; defaults to the first item's department.
 * @param {'portal'|'crm'} [opts.source='crm']
 * @param {string} [opts.postedByUid]
 * @param {boolean} [opts.postToTeam=true]
 * @param {DirectPricing} [opts.directPricing] Exec-entered price — skips the team queue entirely.
 * @returns {Promise<string>} new bid id
 */
export async function createBidPackage({
  accountId,
  name,
  items,
  description = '',
  icon = '🧩',
  department = '',
  source = 'crm',
  postedByUid = '',
  postToTeam = true,
  directPricing = null,
}) {
  if (!accountId) throw new Error('accountId is required');
  const packageName = String(name || '').trim();
  if (!packageName) throw new Error('Give the package a name');

  const packageItems = normalizePackageItems(items);
  if (packageItems.length === 0) throw new Error('Add at least one service to the package');

  const accountFields = await bidAccountFields(accountId);
  const packageDepartment =
    department || packageItems.find((item) => item.department)?.department || 'General';
  const team = departmentToPostedTeam(packageDepartment);
  const deliverableRows = packageItemsToDeliverableRows(packageItems);
  const now = new Date();

  const payload = {
    accountId,
    ...accountFields,
    catalogItemId: '',
    department: packageDepartment,
    name: packageName,
    description: String(description || '').trim(),
    icon: icon || '🧩',
    pricingType: 'caseByCase',
    isPackage: true,
    packageItems,
    standardPricing: { cost: 0, unit: 'item', stripeProductId: '' },
    scope: '',
    deliverableRows,
    teamInput: { scope: '', deliverableRows, internalCostEstimate: '' },
    internalOnly: { notes: '' },
    bidNegotiation: {
      counterClientPrice: '',
      counterInternalCost: '',
      counterNote: '',
      execDecisionNote: '',
    },
    internalCost: '',
    clientListPrice: '',
    markupPercent: '',
    quantity: 1,
    source: source === 'portal' ? 'portal' : 'crm',
    sourceRequestId: '',
    createdByUid: postedByUid || '',
    createdAt: now,
    updatedAt: now,
    ...(directPricing
      ? directPricingFields({ ...directPricing, postedByUid, now })
      : postingFields({ postToTeam, team, postedByUid, now })),
  };

  const ref = await addDoc(collection(db, 'alaCarteBids'), payload);
  return ref.id;
}

/**
 * @param {{ id: string, data: () => Record<string, unknown> }} portalRequestDoc
 * @param {string} [postedByUid]
 * @returns {Promise<string[]>}
 */
export async function createBidsFromPortalRequest(portalRequestDoc, postedByUid = '') {
  const data = portalRequestDoc.data();
  const accountId = typeof data.accountId === 'string' ? data.accountId : '';
  if (!accountId) return [];

  const details = data.details && typeof data.details === 'object' ? data.details : {};
  const cart = Array.isArray(details.cart) ? details.cart : [];
  const caseByCaseLines = cart.filter((line) => line && line.pricingType === 'caseByCase' && line.id);
  if (caseByCaseLines.length === 0) return [];

  const bidIds = [];
  for (const line of caseByCaseLines) {
    const bidId = await createBidFromCatalogItem({
      accountId,
      catalogItemId: line.id,
      source: 'portal',
      sourceRequestId: portalRequestDoc.id,
      postedByUid,
      postToTeam: true,
      quantity: Number(line.quantity) || 1,
    });
    if (bidId) bidIds.push(bidId);
  }
  return bidIds;
}

/**
 * All bids across every account, newest first — exec bids board.
 */
export async function fetchAllBids() {
  try {
    const q = query(collection(db, 'alaCarteBids'), orderBy('createdAt', 'desc'));
    const snap = await getDocs(q);
    return mapSortedBids(snap.docs);
  } catch (e) {
    console.warn('[fetchAllBids] falling back to client-side sort', e);
    const snap = await getDocs(collection(db, 'alaCarteBids'));
    return mapSortedBids(snap.docs);
  }
}

/**
 * @param {string} accountId
 */
export async function fetchBidsForAccount(accountId) {
  if (!accountId) return [];
  try {
    const q = query(
      collection(db, 'alaCarteBids'),
      where('accountId', '==', accountId),
      orderBy('createdAt', 'desc')
    );
    const snap = await getDocs(q);
    return mapSortedBids(snap.docs);
  } catch (e) {
    console.warn('[fetchBidsForAccount] falling back to client-side sort', e);
    const q = query(collection(db, 'alaCarteBids'), where('accountId', '==', accountId));
    const snap = await getDocs(q);
    return mapSortedBids(snap.docs);
  }
}

/**
 * Published quotes for the client portal — returns client-safe projections only.
 * @param {string} accountId
 */
export async function fetchPublishedBidsForAccount(accountId) {
  if (!accountId) return [];
  const q = query(
    collection(db, 'alaCarteBids'),
    where('accountId', '==', accountId),
    where('pricingStatus', '==', PRICING_STATUS.published)
  );
  const snap = await getDocs(q);
  return mapSortedBids(snap.docs).map(sanitizeBidForClient);
}

/**
 * Records the client's decision on a published quote.
 * @param {object} opts
 * @param {string} opts.bidId
 * @param {'accepted'|'declined'|'countered'} opts.status
 * @param {string} [opts.counterPrice]
 * @param {string} [opts.note]
 * @param {string} [opts.respondedByUid]
 * @param {string} [opts.respondedByName]
 */
export async function submitClientBidResponse({
  bidId,
  status,
  counterPrice = '',
  note = '',
  respondedByUid = '',
  respondedByName = '',
}) {
  if (!bidId) throw new Error('bidId is required');
  const valid = [
    CLIENT_RESPONSE_STATUS.accepted,
    CLIENT_RESPONSE_STATUS.declined,
    CLIENT_RESPONSE_STATUS.countered,
  ];
  if (!valid.includes(status)) throw new Error('Invalid response status');
  if (status === CLIENT_RESPONSE_STATUS.countered) {
    const n = parseFloat(String(counterPrice));
    if (!Number.isFinite(n) || n < 0) throw new Error('Enter a valid counter price');
  }
  const snap = await getDoc(doc(db, 'alaCarteBids', bidId));
  if (!snap.exists()) throw new Error('Quote not found');
  const bid = normalizeAlaCarteBid(snap.id, snap.data());
  if (bid.pricingStatus !== PRICING_STATUS.published) {
    throw new Error('This quote is no longer open for responses');
  }

  await updateDoc(doc(db, 'alaCarteBids', bidId), {
    clientResponse: {
      status,
      counterPrice: status === CLIENT_RESPONSE_STATUS.countered ? String(counterPrice).trim() : '',
      note: String(note || '').trim(),
      respondedAt: new Date(),
      respondedByUid,
      respondedByName,
    },
    updatedAt: new Date(),
  });
}

/**
 * @param {string} department
 * @param {string[]} [statuses]
 */
export async function fetchBidsForDepartment(department, statuses) {
  if (!department) return [];
  try {
    const q = query(collection(db, 'alaCarteBids'), where('department', '==', department));
    const snap = await getDocs(q);
    let bids = mapSortedBids(snap.docs);
    if (Array.isArray(statuses) && statuses.length > 0) {
      bids = bids.filter((b) => statuses.includes(b.pricingStatus));
    }
    return bids;
  } catch (e) {
    console.error('[fetchBidsForDepartment]', e);
    return [];
  }
}

/**
 * @param {string} bidId
 */
export async function fetchBidById(bidId) {
  if (!bidId) return null;
  const snap = await getDoc(doc(db, 'alaCarteBids', bidId));
  if (!snap.exists()) return null;
  return normalizeAlaCarteBid(snap.id, snap.data());
}

/**
 * @param {string} requestId
 */
export async function fetchBidsForPortalRequest(requestId) {
  if (!requestId) return [];
  const q = query(collection(db, 'alaCarteBids'), where('sourceRequestId', '==', requestId));
  const snap = await getDocs(q);
  return mapSortedBids(snap.docs);
}

/**
 * @param {string} requestId
 * @param {string[]} bidIds
 */
export async function linkBidIdsToPortalRequest(requestId, bidIds) {
  if (!requestId || !Array.isArray(bidIds) || bidIds.length === 0) return;
  await updateDoc(doc(db, 'portalFulfillmentRequests', requestId), {
    bidIds,
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Permanently removes a bid, and unlinks it from the portal request that spawned it.
 * @param {{ id: string, sourceRequestId?: string }} bid
 */
export async function deleteBid(bid) {
  if (!bid?.id) return;
  await deleteDoc(doc(db, 'alaCarteBids', bid.id));
  if (bid.sourceRequestId) {
    try {
      await updateDoc(doc(db, 'portalFulfillmentRequests', bid.sourceRequestId), {
        bidIds: arrayRemove(bid.id),
        updatedAt: new Date().toISOString(),
      });
    } catch (e) {
      console.warn('[deleteBid] could not unlink portal request', e);
    }
  }
}

function mapSortedBids(docs) {
  const bids = docs.map((d) => normalizeAlaCarteBid(d.id, d.data()));
  bids.sort((a, b) => {
    const ta = toMillis(a.createdAt);
    const tb = toMillis(b.createdAt);
    return tb - ta;
  });
  return bids;
}

function toMillis(value) {
  if (!value) return 0;
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : 0;
}
